import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Settings2, Save, KeyRound } from "lucide-react";
import { toast } from "sonner";

const TZ_OPTIONS = [
  { v: 3, label: "Asia/Qatar (UTC+3)" },
  { v: 4, label: "Asia/Dubai (UTC+4)" },
  { v: 2, label: "Africa/Cairo (UTC+2)" },
  { v: 0, label: "UTC" },
  { v: 5.5, label: "Asia/Kolkata (UTC+5:30)" },
];

const TOGGLES = [
  { key: "notify_new_assignment", label: "New lead assignment", hint: "Notify the agent when a lead is assigned to them" },
  { key: "notify_reassignment", label: "Lead reassignment", hint: "Notify the new owner on manual / Arabic-only reassignment" },
  { key: "notify_missed_callback", label: "Missed callbacks", hint: "Notify the owner when a scheduled callback passes its grace window" },
  { key: "notify_upcoming_callback", label: "Upcoming callback warning", hint: "Warn the owner shortly before a scheduled callback is due" },
  { key: "notify_incomplete_capture", label: "Incomplete capture nudge", hint: "Daily reminder to agents with handled calls missing a disposition" },
  { key: "notify_import_complete", label: "Import completed", hint: "Notify the uploader when a bulk lead import finishes" },
];

/** §8.8 System Configuration — timezone, notification toggles, integration keys. */
export default function SystemConfigForm() {
  const { activeSlug } = useClient();
  const [cfg, setCfg] = useState(null);
  const [gen, setGen] = useState({ region_host: "", client_id: "", client_secret: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/lead-system-config").then((r) => {
      setCfg(r.data);
      setGen({
        region_host: r.data?.genesys?.region_host || "",
        client_id: r.data?.genesys?.client_id || "",
        client_secret: r.data?.genesys?.client_secret || "",
      });
    }).catch(() => toast.error("Failed to load system configuration"));
  }, []);
  useEffect(() => { load(); }, [load, activeSlug]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/lead-system-config", {
        timezone_offset_hours: cfg.timezone_offset_hours,
        timezone_label: cfg.timezone_label,
        notify_new_assignment: cfg.notify_new_assignment,
        notify_reassignment: cfg.notify_reassignment,
        notify_missed_callback: cfg.notify_missed_callback,
        notify_upcoming_callback: cfg.notify_upcoming_callback,
        upcoming_callback_warning_minutes: Number(cfg.upcoming_callback_warning_minutes) || 15,
        notify_incomplete_capture: cfg.notify_incomplete_capture,
        notify_import_complete: cfg.notify_import_complete,
        genesys: gen,
      });
      setCfg(data);
      setGen({
        region_host: data?.genesys?.region_host || "",
        client_id: data?.genesys?.client_id || "",
        client_secret: data?.genesys?.client_secret || "",
      });
      toast.success("System configuration saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <div className="text-slate-400 text-sm p-4">Loading…</div>;

  return (
    <Card data-testid="system-config-form">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2">
          <Settings2 className="w-4 h-4 text-slate-500" /> System Configuration
        </CardTitle>
        <Button size="sm" onClick={save} disabled={saving}
                className="bg-indigo-600 hover:bg-indigo-700" data-testid="system-config-save">
          <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="max-w-xs">
          <Label className="text-[10px] uppercase tracking-wider text-slate-400">Client timezone</Label>
          <Select
            value={String(cfg.timezone_offset_hours)}
            onValueChange={(v) => {
              const opt = TZ_OPTIONS.find((o) => String(o.v) === v);
              setCfg((c) => ({ ...c, timezone_offset_hours: Number(v), timezone_label: opt?.label || `UTC${v >= 0 ? "+" : ""}${v}` }));
            }}
          >
            <SelectTrigger className="h-8 mt-1 text-xs" data-testid="timezone-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TZ_OPTIONS.map((o) => <SelectItem key={o.v} value={String(o.v)}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-slate-400 mt-1">Used for call windows and email schedule times.</p>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">Notifications</div>
          <div className="space-y-2 max-w-lg">
            {TOGGLES.map((t) => (
              <div key={t.key} className="border border-slate-200 rounded px-3 py-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-medium text-slate-800">{t.label}</div>
                    <div className="text-[10px] text-slate-400">{t.hint}</div>
                  </div>
                  <Switch checked={!!cfg[t.key]} onCheckedChange={(v) => setCfg((c) => ({ ...c, [t.key]: v }))}
                          data-testid={`toggle-${t.key}`} />
                </div>
                {t.key === "notify_upcoming_callback" && cfg.notify_upcoming_callback && (
                  <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-400">Warn</Label>
                    <Input type="number" min={1} max={240}
                           value={cfg.upcoming_callback_warning_minutes ?? 15}
                           onChange={(e) => setCfg((c) => ({ ...c, upcoming_callback_warning_minutes: e.target.value }))}
                           className="h-7 w-20 text-xs" data-testid="upcoming-callback-warning-minutes" />
                    <span className="text-[10px] text-slate-400">minutes before the callback is due</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-2">
            <KeyRound className="w-3 h-3" /> Genesys Cloud CX credentials
            <Badge variant="outline" className={`text-[9px] ${cfg.genesys?.configured ? "text-emerald-700 border-emerald-300" : "text-amber-700 border-amber-300"}`}>
              {cfg.genesys?.configured ? "Configured" : "Not configured"}
            </Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-2xl">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">Region host</Label>
              <Input value={gen.region_host} onChange={(e) => setGen((g) => ({ ...g, region_host: e.target.value }))}
                     className="h-8 mt-1 text-xs font-mono" placeholder="mypurecloud.de" data-testid="genesys-region-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">OAuth Client ID</Label>
              <Input value={gen.client_id} onChange={(e) => setGen((g) => ({ ...g, client_id: e.target.value }))}
                     className="h-8 mt-1 text-xs font-mono" data-testid="genesys-client-id-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">OAuth Client Secret</Label>
              <Input type="password" value={gen.client_secret}
                     onChange={(e) => setGen((g) => ({ ...g, client_secret: e.target.value }))}
                     className="h-8 mt-1 text-xs font-mono" data-testid="genesys-secret-input" />
            </div>
          </div>
          <p className="text-[10px] text-slate-400 mt-1.5">
            Stored per client. The secret is shown masked — type a new value to replace it.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
